import React, { useEffect, useMemo, useRef, useState } from "react";
import { productsApi } from "@redux/features/products/productsApi";
import BaseButton from "@reusables/BaseButton";
import BaseInput from "@reusables/BaseInput";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import { Customer } from "@/types/general";
import { ReactComponent as PercentageIcon } from "@assets/icons/ic_percentage.svg";
import { cn, toastError } from "@helpers/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Collapse, Fade, Stack } from "@mui/material";
import { useGetModuleCustomerQuery } from "@redux/features/customers/customersApi";
import { useGetModuleTaxesQuery } from "@redux/features/taxes/taxesApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseLabel from "@reusables/BaseLabel";
import BaseSwitch from "@reusables/BaseSwitch";
import BaseTooltip from "@reusables/BaseTooltip";
import { HelpCircle } from "lucide-react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LineSchema } from "../../../../types";
import BOMSelector from "../../components/BOMSelector";
import DiscountCard from "../../DiscountCard";
import ProductSelectionDropdown, {
    ProductSelectionDropdownHandle,
} from "./ProductSelectionDropdown";
import QuantityFields from "./QuantityFields";
import {
    addProductFormScheme,
    AddProductFormSchemeTyping,
    AddProductLineMutationPayload,
    buildCreateDraftValues,
    buildEditDraftValues,
    CreateCarryOver,
    DiscountOption,
    DraftFinishResult,
    DraftMode,
    getRememberedTaxId,
    getTaxOptionById,
    getTaxStorageKey,
} from "../addProductDraftShared";
import { buildDiscountOptions, selectBestDiscount } from "../discountSelection";
import dayjs from "dayjs";

interface AddProductDraftFormProps {
    mode: DraftMode;
    selectedLine?: LineSchema;
    customer?: Customer.Root;
    initialCarryOver: CreateCarryOver;
    onCommitLine: (newOrder: AddProductLineMutationPayload) => void;
    onDraftFinished: (result: DraftFinishResult) => void;
    onSubmittingChange?: (isSubmitting: boolean) => void;
}

export default function AddProductDraftForm({
    mode,
    selectedLine,
    customer,
    initialCarryOver,
    onCommitLine,
    onDraftFinished,
    onSubmittingChange,
}: AddProductDraftFormProps): JSX.Element {
    const { t } = useTranslation("", {
        keyPrefix: "sales.orders.general.orders.modals.addProduct",
    });

    if (mode === "edit" && !selectedLine) {
        throw new Error("AddProductDraftForm requires selectedLine in edit mode.");
    }

    const dispatch = useAppDispatch();
    const baseCurrency = useAppSelector((state) => state.auth.user?.company?.currency.symbol);
    const companyId = useAppSelector((state) => state.auth.user?.company?.id);

    const [discountOptions, setDiscountOptions] = useState<DiscountOption[]>([]);
    const [selectedDiscount, setSelectedDiscount] = useState<DiscountOption>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const productDropdownRef = useRef<ProductSelectionDropdownHandle>(null);

    const taxStorageKey = getTaxStorageKey(companyId);
    const rememberedTaxId = useMemo(() => getRememberedTaxId(taxStorageKey), [taxStorageKey]);
    const createDraftTaxId = initialCarryOver.taxId ?? rememberedTaxId;

    // RTKQ data lives at the draft-form boundary on purpose. The modal shell should not fetch
    // draft-specific data just to drill it down, and remounting this component for add-more
    // reuses RTKQ cache for stable args without reintroducing field-level reset logic.
    const { data: taxesOptions, isLoading: isTaxesOptionsLoading } = useGetModuleTaxesQuery("sale");
    const { data: selectedCustomer } = useGetModuleCustomerQuery(
        { module: "sale", id: customer?.id as number },
        { skip: !customer }
    );

    // Every mounted instance of this component represents exactly one logical line draft.
    // That makes RHF state, refs and child local state align with the lifetime of one record.
    const form = useForm<AddProductFormSchemeTyping>({
        resolver: zodResolver(addProductFormScheme),
        defaultValues:
            mode === "edit"
                ? buildEditDraftValues(selectedLine as LineSchema)
                : buildCreateDraftValues(initialCarryOver),
    });

    const { control, formState, setValue } = form;

    const selectedProduct = useWatch({
        control,
        name: "product",
    });

    const quantity = useWatch({
        control,
        name: "quantity",
    });

    const shouldUseBOM = useWatch({
        control,
        name: "should_use_bom",
    });

    const hasBOM = selectedProduct?.has_bom ?? false;

    // Bubbling submit-state changes back to the modal shell so the modal-level loading overlay
    // reflects the current mutation lifecycle of this draft instance.
    useEffect(() => {
        onSubmittingChange?.(isSubmitting);

        return () => {
            onSubmittingChange?.(false);
        };
    }, [isSubmitting, onSubmittingChange]);

    // A newly mounted create draft should behave like a fresh "add next product" session:
    // placing focus back on the product field so typing/scanning can resume immediately.
    useEffect(() => {
        if (mode !== "create") return;

        const focusTimer = window.setTimeout(() => {
            productDropdownRef.current?.focus();
        }, 100);

        return () => {
            window.clearTimeout(focusTimer);
        };
    }, [mode]);

    // Tax options arrive asynchronously from RTKQ. Backfill only the tax field for the current
    // create draft once the matching option exists, without resetting the rest of the form or
    // overwriting a tax that the user already touched in this draft session.
    useEffect(() => {
        if (mode !== "create") return;
        if (!createDraftTaxId) return;
        if (formState.dirtyFields.tax) return;
        if (form.getValues("tax")) return;

        const recalledTax = getTaxOptionById(taxesOptions, createDraftTaxId);
        if (!recalledTax) return;

        setValue("tax", recalledTax);
    }, [createDraftTaxId, form, formState.dirtyFields.tax, mode, setValue, taxesOptions]);

    const handleDiscountSelect = (value: DiscountOption): void => {
        if (value.id === selectedDiscount?.id) {
            setSelectedDiscount(undefined);
            setValue("discount", undefined);
            setValue("unit_price", selectedProduct?.prices?.selling_price);
        } else {
            setSelectedDiscount(value);
            setValue("discount", value.discount);
            setValue("unit_price", value.salePrice ?? selectedProduct?.prices?.selling_price);
        }
    };

    // Recompute candidates whenever their customer, product or quantity context changes. A manual
    // selection remains intact until one of those inputs changes.
    useEffect(() => {
        if (mode === "edit") return;

        const now = dayjs();
        const nextDiscountOptions = buildDiscountOptions({
            customer: selectedCustomer,
            product: selectedProduct,
            now,
        });
        const nextSelectedDiscount = selectBestDiscount(nextDiscountOptions, quantity ?? 0, now);

        if (nextSelectedDiscount) {
            setValue("discount", nextSelectedDiscount.discount);
            setValue(
                "unit_price",
                nextSelectedDiscount.salePrice ?? selectedProduct?.prices?.selling_price
            );
        } else {
            setValue("discount", undefined);
            setValue("unit_price", selectedProduct?.prices?.selling_price);
        }

        setDiscountOptions(nextDiscountOptions);
        setSelectedDiscount(nextSelectedDiscount);
    }, [mode, quantity, selectedCustomer, selectedProduct, setValue]);

    const finishDraft = (data: AddProductFormSchemeTyping) => {
        if (mode === "edit" || !data.add_more) {
            onDraftFinished({ action: "close" });
            return;
        }

        // Only explicitly shared create-mode preferences are carried into the next draft.
        // Everything else comes from a fresh RHF instance to avoid cross-line artefacts.
        onDraftFinished({
            action: "continue",
            carryOver: {
                add_more: true,
                taxId: data.tax?.id,
            },
        });
    };

    const onSubmit = form.handleSubmit(async (data) => {
        setIsSubmitting(true);

        try {
            const product = await dispatch(
                productsApi.endpoints.getModuleProduct.initiate({
                    module: "sale",
                    id: data.product.id,
                })
            ).unwrap();

            const locations = product.locations ?? [];
            const inStock = locations.reduce((acc, curr) => acc + (curr.in_stock ?? 0), 0);

            onCommitLine({
                ...data,
                in_stock: inStock,
                min_sale_quantity: product.min_sale_quantity,
            });
            finishDraft(data);
        } catch (e) {
            toastError(e);
            console.error("Unable to load the selected product before adding the order line", e);
        } finally {
            setIsSubmitting(false);
        }
    });

    return (
        <FormProvider {...form}>
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="modal-title">
                    {mode === "edit" ? selectedLine?.product.name : t("heading")}
                </div>
                <BaseInputsGrid cols={2}>
                    <ProductSelectionDropdown
                        ref={productDropdownRef}
                        selectedLine={selectedLine}
                    />

                    <QuantityFields control={control} selectedLine={selectedLine} />

                    <Controller
                        name="discount"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                onChange={(event) => {
                                    setSelectedDiscount(undefined);
                                    field.onChange(event);
                                }}
                                error={fieldState.error}
                                label={t("fields.discount.label")}
                                placeholder={t("fields.discount.placeholder")}
                                type="number"
                                icon={{ right: { el: <PercentageIcon />, offset: "38px" } }}
                            />
                        )}
                    />

                    <Controller
                        name="tax"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseDropdown
                                {...field}
                                onChange={(value) => {
                                    if (mode === "create" && value) {
                                        localStorage.setItem(taxStorageKey, JSON.stringify(value));
                                    }

                                    field.onChange(value);
                                }}
                                error={fieldState.error}
                                label={t("fields.tax.label")}
                                placeholder={t("fields.tax.placeholder")}
                                options={taxesOptions}
                                getter={{
                                    key: (opt) => opt.id,
                                    label: (opt) => `${opt.rate}%`,
                                    caption: (opt) => opt.name ?? "",
                                }}
                                isLoading={isTaxesOptionsLoading}
                            />
                        )}
                    />

                    <Fade in={!shouldUseBOM}>
                        <div>
                            <Controller
                                name="unit_price"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <BaseInput
                                        {...field}
                                        error={fieldState.error}
                                        label={t("fields.unitPrice.label")}
                                        placeholder={t("fields.unitPrice.placeholder")}
                                        type="number"
                                        step="any"
                                        icon={{
                                            right: {
                                                el: (
                                                    <span className="w-max h-max text-purple-400 font-thin">
                                                        {baseCurrency}
                                                    </span>
                                                ),
                                                offset: "38px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Fade>

                    <Fade in={!shouldUseBOM}>
                        <div>
                            <Controller
                                name="comment"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <div className="flex flex-col">
                                        <Stack
                                            direction={"row"}
                                            alignItems={"center"}
                                            spacing={1}
                                            className={"pointer-events-auto"}
                                        >
                                            <BaseLabel>{t("fields.comment.label")}</BaseLabel>
                                            <BaseTooltip
                                                title={t("fields.comment.whatIsComment") + ""}
                                                placement={"top"}
                                            >
                                                <HelpCircle
                                                    className="mb-2"
                                                    size={16}
                                                    color={"#686868"}
                                                />
                                            </BaseTooltip>
                                        </Stack>
                                        <BaseInput
                                            {...field}
                                            error={fieldState.error}
                                            placeholder={t("fields.comment.placeholder")}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                    </Fade>
                </BaseInputsGrid>
                {selectedProduct && selectedCustomer && (
                    <Fade in={!!selectedProduct} timeout={500} key={selectedProduct.id}>
                        <div>
                            <DiscountCard
                                discounts={discountOptions}
                                onDiscountSelect={handleDiscountSelect}
                                selectedDiscount={selectedDiscount}
                            />
                        </div>
                    </Fade>
                )}
                {mode === "create" && (
                    <>
                        <Collapse in={!!form.formState.errors.selected_bom}>
                            <Alert severity={"error"}>{t("bom.validation.noBOMSelected")}</Alert>
                        </Collapse>

                        <Stack direction={"row"}>
                            <div
                                className={cn(
                                    `grow border-[1px] border-solid rounded-lg bg-white transition-all overflow-hidden`,
                                    form.formState.errors.selected_bom
                                        ? `border-inputs-border-error`
                                        : `border-inputs-border-default`,
                                    hasBOM
                                        ? "hover:border-inputs-border-focused"
                                        : "pointer-events-none opacity-30"
                                )}
                            >
                                <Controller
                                    control={control}
                                    name="should_use_bom"
                                    render={({ field }) => (
                                        <div
                                            className={cn(
                                                `flex flex-row items-center justify-between p-3`,
                                                hasBOM &&
                                                    "cursor-pointer transition-all hover:bg-purple-100 text-accent"
                                            )}
                                            onClick={() => {
                                                if (hasBOM) field.onChange(!field.value);
                                            }}
                                        >
                                            <Stack
                                                direction={"row"}
                                                alignItems={"center"}
                                                spacing={1}
                                                className={"pointer-events-auto"}
                                            >
                                                <span>{t("bom.label")}</span>
                                                <BaseTooltip
                                                    title={t("bom.whatIsBOM") + ""}
                                                    placement={"top"}
                                                >
                                                    <HelpCircle size={16} color={"#686868"} />
                                                </BaseTooltip>
                                            </Stack>
                                            <BaseSwitch
                                                checked={field.value}
                                                className={cn(
                                                    "focus:outline-offset-1 focus:color-primary-500 focus:shadow-outline-primary-500 focus:border-primary-500 focus:ring-primary-500 focus:ring-offset-primary-500"
                                                )}
                                            />
                                        </div>
                                    )}
                                />
                                {!!selectedProduct && (
                                    <Collapse in={shouldUseBOM}>
                                        <BOMSelector
                                            active={shouldUseBOM}
                                            product_id={selectedProduct.id}
                                            control={control}
                                        />
                                    </Collapse>
                                )}
                            </div>
                        </Stack>
                    </>
                )}
                <div className="flex flex-column items-end">
                    {mode === "create" && (
                        <Controller
                            name={"add_more"}
                            control={control}
                            render={({ field }) => (
                                <div
                                    className="inline-flex items-center space-x-2 mb-2 user-select-none text-accent"
                                    onClick={() => field.onChange(!field.value)}
                                >
                                    <BaseSwitch checked={field.value ?? false} />
                                    <span>{t("addMore")}</span>
                                </div>
                            )}
                        />
                    )}
                    <BaseButton
                        text={mode === "edit" ? t("buttons.save") : t("buttons.add")}
                        size="md"
                        className="w-full"
                    />
                </div>
            </form>
        </FormProvider>
    );
}
